import { ReactNode, useEffect, useLayoutEffect, useMemo, useState } from "react";

type FeatureRule = {
  page_id: string;
  scope: "page" | "component";
  component_selector: string;
  label: string;
  paid: boolean;
  product_id: string | null;
  product_name: string | null;
  amount_minor: number | null;
  product_active: number | null;
  granted: boolean;
  canPurchase: boolean;
};

function browserVisitorId(): string {
  try {
    const key = "xm-browser-visitor-id-v1";
    const existing = localStorage.getItem(key);
    if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
    const created = crypto.randomUUID();
    localStorage.setItem(key, created);
    return created;
  } catch {
    return "";
  }
}

function makePaywall(label: string, rule: FeatureRule): HTMLDivElement {
  const overlay = document.createElement("div");
  overlay.className = "feature-access-overlay";
  const content = document.createElement("div");
  content.className = "feature-access-overlay-content";
  const title = document.createElement("b");
  title.textContent = `${label}需要购买后使用`;
  content.append(title);
  const price = document.createElement("span");
  price.textContent = rule.amount_minor === null ? "" : `¥${(rule.amount_minor / 100).toFixed(2)}`;
  content.append(price);
  if (rule.canPurchase && rule.product_id) {
    const link = document.createElement("a");
    link.href = `/checkout/${encodeURIComponent(rule.product_id)}`;
    link.textContent = "立即购买";
    content.append(link);
  } else {
    const unavailable = document.createElement("span");
    unavailable.textContent = "该收费项目暂未上架";
    content.append(unavailable);
  }
  overlay.append(content);
  return overlay;
}

export function FeatureAccessBoundary({ pageId, children }: { pageId: string; children: ReactNode }) {
  const [rules, setRules] = useState<FeatureRule[]>([]);
  const [loadedPageId, setLoadedPageId] = useState("");
  const loaded = loadedPageId === pageId;
  const visitorId = useMemo(browserVisitorId, []);

  useEffect(() => {
    let cancelled = false;
    if (!visitorId) { setLoadedPageId(pageId); return; }
    void fetch(`/api/feature-access?visitorId=${encodeURIComponent(visitorId)}`)
      .then(async (response) => {
        if (!response.ok) return null;
        return await response.json() as { rules?: FeatureRule[] };
      })
      .then((data) => { if (!cancelled) { setRules(data?.rules || []); setLoadedPageId(pageId); } })
      .catch(() => { if (!cancelled) setLoadedPageId(pageId); });
    return () => { cancelled = true; };
  }, [visitorId, pageId]);

  const pageRule = useMemo(() => rules.find((rule) => rule.page_id === pageId && rule.scope === "page"), [rules, pageId]);
  const componentRules = useMemo(() => rules.filter((rule) => rule.page_id === pageId && rule.scope === "component"), [rules, pageId]);

  useLayoutEffect(() => {
    const workbench = document.querySelector<HTMLElement>(".tool-page .workbench");
    if (!workbench) return;
    workbench.classList.toggle("feature-access-loading", !loaded);
    workbench.classList.remove("feature-access-page-locked");
    workbench.querySelectorAll(".feature-access-overlay").forEach((item) => item.remove());
    workbench.querySelectorAll(".feature-access-exception, .feature-access-component-locked").forEach((item) => item.classList.remove("feature-access-exception", "feature-access-component-locked"));

    const pageBlocked = Boolean(pageRule?.paid && !pageRule.granted);
    if (pageBlocked && pageRule) {
      workbench.classList.add("feature-access-page-locked");
      workbench.append(makePaywall(pageRule.label, pageRule));
    }
    for (const rule of componentRules) {
      let targets: NodeListOf<HTMLElement>;
      try { targets = workbench.querySelectorAll<HTMLElement>(rule.component_selector); } catch { continue; }
      targets.forEach((target) => {
        if (pageBlocked) target.classList.add("feature-access-exception");
        if (rule.paid && !rule.granted) {
          target.classList.add("feature-access-component-locked");
          target.append(makePaywall(rule.label, rule));
        }
      });
    }

    return () => {
      workbench.classList.remove("feature-access-page-locked");
      workbench.querySelectorAll(".feature-access-overlay").forEach((item) => item.remove());
      workbench.querySelectorAll(".feature-access-exception, .feature-access-component-locked").forEach((item) => item.classList.remove("feature-access-exception", "feature-access-component-locked"));
    };
  }, [pageRule, componentRules, loaded]);

  return <>{children}</>;
}
